import pako from 'pako';

/**
 * InstallationParser - Parses Universal Robots .installation files
 * 
 * Architecture:
 * - Decompresses gzipped .installation files
 * - Parses XML structure
 * - Extracts features with their coordinate frames
 */
export class InstallationParser {
  constructor() {
    this.fileName = null;
    this.xmlDoc = null;
    this.features = [];
    this.tcpSettings = null; // Will store parsed TCP data
  }

  /**
   * Parse a .installation file
   * @param {File} file - The .installation file to parse
   * @returns {Promise<Object>} Parsed installation data
   */
  async parseFile(file) {
    this.fileName = file.name;
    
    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Use parseData for the actual parsing
      return this.parseData(uint8Array, file.name);
      
    } catch (error) {
      console.error('Error parsing .installation file:', error);
      throw new Error(`Failed to parse .installation file: ${error.message}`);
    }
  }

  /**
   * Parse installation data from Uint8Array (for robot downloads or other binary data)
   * @param {Uint8Array} uint8Array - The compressed or uncompressed installation data
   * @param {string} fileName - Optional filename
   * @returns {Object} Parsed data
   */
  parseData(uint8Array, fileName = 'unknown.installation') {
    this.fileName = fileName;
    
    try {
      let decompressed;
      
      // Try to decompress - if it fails, assume it's already uncompressed XML
      console.log('Decompressing .installation file...');
      try {
        decompressed = pako.inflate(uint8Array, { to: 'string' });
        console.log('Decompression successful');
      } catch (decompressError) {
        // If decompression fails, try treating it as plain text XML
        console.log('File not gzipped, treating as plain XML');
        const decoder = new TextDecoder('utf-8');
        decompressed = decoder.decode(uint8Array);
      }
      
      // Parse XML
      const parser = new DOMParser();
      this.xmlDoc = parser.parseFromString(decompressed, 'text/xml');
      
      // Check for parsing errors
      const parserError = this.xmlDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('XML parsing error: ' + parserError.textContent);
      }
      
      // Extract features
      const features = this.extractFeatures();
      
      // Extract TCP settings
      this.tcpSettings = this.extractTCPSettings();
      
      return {
        fileName: this.fileName,
        features: features,
        featureCount: features.length,
        tcpSettings: this.tcpSettings
      };
      
    } catch (error) {
      console.error('Error parsing .installation data:', error);
      throw new Error(`Failed to parse .installation data: ${error.message}`);
    }
  }

  /**
   * Extract all features from the XML document
   * @returns {Array<Object>} Array of feature objects
   */
  extractFeatures() {
    const features = [];
    
    // Add Base feature (always at origin)
    features.push({
      name: 'Base',
      position: { x: 0, y: 0, z: 0 },
      rotation: { rx: 0, ry: 0, rz: 0 },
      isBase: true
    });
    
    // Find all GeomPoseNode elements under Features (capital F)
    const featuresTag = this.xmlDoc.querySelector('Features');
    if (!featuresTag) {
      console.warn('No Features tag found in installation file');
      return features;
    }
    
    // Get direct child GeomPoseNode elements (not nested ones)
    const geomPoseNodes = Array.from(featuresTag.children).filter(child => 
      child.tagName === 'GeomPoseNode'
    );
    console.log(`Found ${geomPoseNodes.length} feature nodes`);
    
    geomPoseNodes.forEach((node, index) => {
      const feature = this.parseFeature(node, index);
      if (feature) {
        features.push(feature);
      }
    });
    
    return features;
  }

  /**
   * Extract TCP settings from the installation file
   * @returns {Object} TCP settings with activePose and available poses
   */
  extractTCPSettings() {
    const tcpSettingsTag = this.xmlDoc.querySelector('TCPSettings');
    if (!tcpSettingsTag) {
      console.warn('No TCPSettings tag found in installation file');
      return null;
    }

    const activePose = tcpSettingsTag.getAttribute('activePose');
    const availablePoses = [];

    const tcpElements = tcpSettingsTag.querySelectorAll('tcp');
    tcpElements.forEach((tcpElement) => {
      const id = tcpElement.getAttribute('id');
      const name = tcpElement.getAttribute('name');
      const offsetStr = tcpElement.getAttribute('offset');
      
      if (name && offsetStr) {
        // Parse offset string "x, y, z, rx, ry, rz"
        const offsetValues = offsetStr.split(',').map(v => parseFloat(v.trim()));
        if (offsetValues.length === 6) {
          availablePoses.push({
            id: id,
            name: name,
            offset: {
              x: offsetValues[0],
              y: offsetValues[1],
              z: offsetValues[2],
              rx: offsetValues[3],
              ry: offsetValues[4],
              rz: offsetValues[5]
            }
          });
        }
      }
    });

    return {
      activePose: activePose,
      availablePoses: availablePoses
    };
  }

  /**
   * Get the active TCP offset, with fallback to TCP_Vectis default or standard TCP
   * @returns {Object} TCP offset {x, y, z, rx, ry, rz}
   */
  getTCPOffset() {
    // Default TCP_Vectis offset (fallback)
    const defaultOffset = {
      x: 0.002880472034598727,
      y: -0.02918177960863152,
      z: 0.47236807083603005,
      rx: -0.8846974644582652,
      ry: 0.010026373014888897,
      rz: -0.005155957771068098
    };

    // If no TCP settings parsed, return default
    if (!this.tcpSettings || !this.tcpSettings.availablePoses || this.tcpSettings.availablePoses.length === 0) {
      console.log('No TCP settings found, using default TCP_Vectis offset');
      return defaultOffset;
    }

    // Try to find the active pose
    if (this.tcpSettings.activePose) {
      const activeTCP = this.tcpSettings.availablePoses.find(
        tcp => tcp.name === this.tcpSettings.activePose
      );
      if (activeTCP) {
        console.log(`Using active TCP: ${this.tcpSettings.activePose}`, activeTCP.offset);
        return activeTCP.offset;
      }
    }

    // Try to find TCP_Vectis
    const vectisTCP = this.tcpSettings.availablePoses.find(tcp => tcp.name === 'TCP_Vectis');
    if (vectisTCP) {
      console.log('Using TCP_Vectis offset', vectisTCP.offset);
      return vectisTCP.offset;
    }

    // Fall back to first available TCP
    if (this.tcpSettings.availablePoses.length > 0) {
      console.log(`Using first available TCP: ${this.tcpSettings.availablePoses[0].name}`, this.tcpSettings.availablePoses[0].offset);
      return this.tcpSettings.availablePoses[0].offset;
    }

    // Ultimate fallback
    console.log('Using default TCP_Vectis offset (no TCP found)');
    return defaultOffset;
  }

  /**
   * Parse a single feature from GeomPoseNode
   * @param {Element} node - The GeomPoseNode XML element
   * @param {number} index - Index in the feature list
   * @returns {Object} Parsed feature data
   */
  parseFeature(node, index) {
    try {
      // Look for nested GeomPoseNode (actual feature definition)
      const featureNode = node.querySelector('GeomPoseNode');
      if (!featureNode) {
        console.warn(`No nested GeomPoseNode found at index ${index}`);
        return null;
      }
      
      // Get feature name
      const name = featureNode.getAttribute('name') || `Feature_${index + 1}`;
      
      // Get tool position - look for <toolPosition value="x, y, z"/> element
      const toolPositionElement = featureNode.querySelector('toolPosition');
      if (!toolPositionElement) {
        console.warn(`No toolPosition element found for feature ${name}`);
        return null;
      }
      
      const toolPositionValue = toolPositionElement.getAttribute('value');
      if (!toolPositionValue) {
        console.warn(`No toolPosition value found for feature ${name}`);
        return null;
      }
      
      // Parse the position array "x, y, z"
      const posValues = toolPositionValue.split(',').map(v => parseFloat(v.trim()));
      const x = posValues[0] || 0;
      const y = posValues[1] || 0;
      const z = posValues[2] || 0;
      
      // Get tool axis angle - look for <toolAxisAngle value="rx, ry, rz"/> element
      const toolAxisAngleElement = featureNode.querySelector('toolAxisAngle');
      if (!toolAxisAngleElement) {
        console.warn(`No toolAxisAngle element found for feature ${name}`);
        return null;
      }
      
      const toolAxisAngleValue = toolAxisAngleElement.getAttribute('value');
      if (!toolAxisAngleValue) {
        console.warn(`No toolAxisAngle value found for feature ${name}`);
        return null;
      }
      
      // Parse the rotation array "rx, ry, rz"
      const rotValues = toolAxisAngleValue.split(',').map(v => parseFloat(v.trim()));
      const rx = rotValues[0] || 0;
      const ry = rotValues[1] || 0;
      const rz = rotValues[2] || 0;
      
      return {
        name: name,
        position: { x, y, z },
        rotation: { rx, ry, rz },
        isBase: false,
        element: featureNode // Keep reference to the actual GeomPoseNode
      };
      
    } catch (error) {
      console.warn('Failed to parse feature:', error);
      return null;
    }
  }

  /**
   * Update a feature's position
   * @param {number} index - Index of the feature (0 = Base, 1+ = actual features)
   * @param {Object} position - New position {x, y, z}
   */
  updateFeaturePosition(index, position) {
    if (index === 0) {
      throw new Error('Cannot modify Base feature');
    }

    // Find all GeomPoseNode elements (actual features are index-1 since Base is index 0)
    const featuresTag = this.xmlDoc.querySelector('Features');
    const geomPoseNodes = Array.from(featuresTag.children).filter(child => 
      child.tagName === 'GeomPoseNode'
    );
    
    const featureIndex = index - 1; // Adjust for Base
    if (featureIndex < 0 || featureIndex >= geomPoseNodes.length) {
      throw new Error(`Invalid feature index: ${index}`);
    }
    
    const node = geomPoseNodes[featureIndex];
    const featureNode = node.querySelector('GeomPoseNode');
    
    if (!featureNode) {
      throw new Error(`Feature node not found at index ${index}`);
    }
    
    // Update toolPosition
    const toolPositionElement = featureNode.querySelector('toolPosition');
    if (toolPositionElement) {
      const value = `${position.x}, ${position.y}, ${position.z}`;
      toolPositionElement.setAttribute('value', value);
    }
  }

  /**
   * Update a feature's rotation
   * @param {number} index - Index of the feature (0 = Base, 1+ = actual features)
   * @param {Object} rotation - New rotation {rx, ry, rz}
   */
  updateFeatureRotation(index, rotation) {
    if (index === 0) {
      throw new Error('Cannot modify Base feature');
    }

    // Find all GeomPoseNode elements (actual features are index-1 since Base is index 0)
    const featuresTag = this.xmlDoc.querySelector('Features');
    const geomPoseNodes = Array.from(featuresTag.children).filter(child => 
      child.tagName === 'GeomPoseNode'
    );
    
    const featureIndex = index - 1; // Adjust for Base
    if (featureIndex < 0 || featureIndex >= geomPoseNodes.length) {
      throw new Error(`Invalid feature index: ${index}`);
    }
    
    const node = geomPoseNodes[featureIndex];
    const featureNode = node.querySelector('GeomPoseNode');
    
    if (!featureNode) {
      throw new Error(`Feature node not found at index ${index}`);
    }
    
    // Update toolAxisAngle
    const toolAxisAngleElement = featureNode.querySelector('toolAxisAngle');
    if (toolAxisAngleElement) {
      const value = `${rotation.rx}, ${rotation.ry}, ${rotation.rz}`;
      toolAxisAngleElement.setAttribute('value', value);
    }
  }

  /**
   * Create a new feature
   * @param {string} name - Feature name
   * @returns {Object} The new feature object
   */
  createFeature(name) {
    const featuresTag = this.xmlDoc.querySelector('Features');
    if (!featuresTag) {
      throw new Error('No Features tag found in installation file');
    }

    // Create the outer GeomPoseNode
    const outerNode = this.xmlDoc.createElement('GeomPoseNode');
    
    // Create the inner GeomPoseNode with the feature data
    const innerNode = this.xmlDoc.createElement('GeomPoseNode');
    innerNode.setAttribute('name', name);
    
    // Create toolPosition element
    const toolPosition = this.xmlDoc.createElement('toolPosition');
    toolPosition.setAttribute('value', '0, 0, 0');
    innerNode.appendChild(toolPosition);
    
    // Create toolAxisAngle element
    const toolAxisAngle = this.xmlDoc.createElement('toolAxisAngle');
    toolAxisAngle.setAttribute('value', '0, 0, 0');
    innerNode.appendChild(toolAxisAngle);
    
    // Add inner node to outer node
    outerNode.appendChild(innerNode);
    
    // Add to Features tag
    featuresTag.appendChild(outerNode);
    
    return {
      name: name,
      position: { x: 0, y: 0, z: 0 },
      rotation: { rx: 0, ry: 0, rz: 0 },
      isBase: false,
      element: innerNode
    };
  }

  /**
   * Delete a feature
   * @param {number} index - Index of the feature (0 = Base, 1+ = actual features)
   */
  deleteFeature(index) {
    if (index === 0) {
      throw new Error('Cannot delete Base feature');
    }

    const featuresTag = this.xmlDoc.querySelector('Features');
    const geomPoseNodes = Array.from(featuresTag.children).filter(child => 
      child.tagName === 'GeomPoseNode'
    );
    
    const featureIndex = index - 1; // Adjust for Base
    if (featureIndex < 0 || featureIndex >= geomPoseNodes.length) {
      throw new Error(`Invalid feature index: ${index}`);
    }
    
    // Remove the outer GeomPoseNode
    const nodeToRemove = geomPoseNodes[featureIndex];
    featuresTag.removeChild(nodeToRemove);
  }

  /**
   * Update a feature's name
   * @param {number} index - Index of the feature (0 = Base, 1+ = actual features)
   * @param {string} name - New name
   */
  updateFeatureName(index, name) {
    if (index === 0) {
      throw new Error('Cannot modify Base feature');
    }

    const featuresTag = this.xmlDoc.querySelector('Features');
    const geomPoseNodes = Array.from(featuresTag.children).filter(child => 
      child.tagName === 'GeomPoseNode'
    );
    
    const featureIndex = index - 1; // Adjust for Base
    if (featureIndex < 0 || featureIndex >= geomPoseNodes.length) {
      throw new Error(`Invalid feature index: ${index}`);
    }
    
    const node = geomPoseNodes[featureIndex];
    const featureNode = node.querySelector('GeomPoseNode');
    
    if (!featureNode) {
      throw new Error(`Feature node not found at index ${index}`);
    }
    
    featureNode.setAttribute('name', name);
  }

  /**
   * Get the XML document as a string
   * @returns {string} XML string
   */
  getXML() {
    if (!this.xmlDoc) {
      throw new Error('No XML document loaded');
    }
    
    return new XMLSerializer().serializeToString(this.xmlDoc);
  }
}
